export type Vec2 = { x: number; y: number };

export type EvidenceKey =
  | 'observe'
  | 'nutrition'
  | 'respiration'
  | 'stimulus'
  | 'growth'
  | 'reproduction'
  | 'heredityVariation'
  | 'waste'
  | 'cells';

export type ClockId = 'seedlings' | 'lithops' | 'seed' | 'flower' | 'duck';
export type Mode = 'world' | 'microscope';

export interface ClockState {
  t: number;
  active: boolean;
}

export interface PlayerState extends Vec2 {
  vx: number;
  vy: number;
  dir: number;
}

export interface CameraState extends Vec2 {
  z: number;
  tx: number;
  ty: number;
  tz: number;
}

export interface DuckState extends Vec2 {
  homeX: number;
  homeY: number;
  vx: number;
  vy: number;
  startledUntil: number;
  swimmingAway: boolean;
}

export interface AppState {
  w: number;
  h: number;
  dpr: number;
  last: number;
  mode: Mode;
  player: PlayerState;
  camera: CameraState;
  clocks: Record<ClockId, ClockState>;
  evidence: Partial<Record<EvidenceKey, true>>;
  evidenceQueue: EvidenceKey[];
  hintUntil: number;
  mimosaTouchedAt: number;
  seedlingSeenAt: number | null;
  duckWatchMs: number;
  duckFeedWatchMs: number;
  duckRestWatchMs: number;
  duckFamilyWatchMs: number;
  duckWasteSeen: boolean;
  duck: DuckState;
  lithopsCompared: boolean;
  microscopeHintShown: boolean;
  microscopeOpenedAt: number;
}

export interface WorldObject {
  id: string;
  x: number;
  y: number;
  r: number;
  clock?: ClockId;
}

export const WORLD = { w: 3000, h: 1850 } as const;

export const OBJECTS = {
  entrance: { id: 'entrance', x: 480, y: 1460, r: 110 },
  seedlings: { id: 'seedlings', x: 920, y: 1350, r: 190, clock: 'seedlings' },
  mimosa: { id: 'mimosa', x: 1190, y: 1010, r: 120 },
  lithops: { id: 'lithops', x: 760, y: 640, r: 118, clock: 'lithops' },
  stone: { id: 'stone', x: 900, y: 650, r: 82 },
  seed: { id: 'seed', x: 1640, y: 1330, r: 160, clock: 'seed' },
  flower: { id: 'flower', x: 1690, y: 760, r: 180, clock: 'flower' },
  pond: { id: 'pond', x: 2420, y: 650, r: 500 },
  duck: { id: 'duck', x: 2440, y: 620, r: 120, clock: 'duck' },
  duckNest: { id: 'duckNest', x: 2360, y: 1040, r: 155, clock: 'duck' },
  microscope: { id: 'microscope', x: 2220, y: 1370, r: 175 }
} satisfies Record<string, WorldObject>;

export const CORE_EVIDENCE: EvidenceKey[] = [
  'nutrition',
  'respiration',
  'stimulus',
  'growth',
  'reproduction',
  'heredityVariation',
  'waste'
];

export const EVIDENCE_TEXT: Record<EvidenceKey, string> = {
  observe: '观察要有明确目的，按顺序进行，做好记录；耗时较长时要有计划和耐心。',
  nutrition: '生物的生活需要营养。',
  respiration: '生物能进行呼吸。',
  stimulus: '生物能对外界刺激作出反应。',
  growth: '生物能生长和发育。',
  reproduction: '生物能繁殖。',
  heredityVariation: '亲子间有相似也有差异，这就是遗传和变异。',
  waste: '生物能排出体内产生的废物。',
  cells: '除病毒外，生物都是由细胞构成的。'
};

export const EVIDENCE_MARKUP: Partial<Record<EvidenceKey, string>> = {
  observe: '观察要有<span class="concept-key">目的</span>、按顺序并做好<span class="concept-key">记录</span>；耗时较长时要有计划和耐心。',
  nutrition: '生物的生活需要<span class="concept-key">营养</span>。',
  respiration: '生物能进行<span class="concept-key">呼吸</span>。',
  stimulus: '生物能对外界<span class="concept-key">刺激</span>作出<span class="concept-key">反应</span>。',
  growth: '生物能<span class="concept-key">生长和发育</span>。',
  reproduction: '生物能<span class="concept-key">繁殖</span>。',
  heredityVariation: '亲子间的相似是<span class="concept-key">遗传</span>；个体间的差异是<span class="concept-key">变异</span>。',
  waste: '生物能排出体内产生的<span class="concept-key">废物</span>。',
  cells: '除病毒外，生物都是由<span class="concept-key">细胞</span>构成的。'
};

export const initialClocks = (): Record<ClockId, ClockState> => ({
  seedlings: { t: 0.06, active: false },
  lithops: { t: 0.12, active: false },
  seed: { t: 0.05, active: false },
  flower: { t: 0.12, active: false },
  duck: { t: 0.18, active: false }
});

export const clamp = (v: number, a = 0, b = 1): number => Math.max(a, Math.min(b, v));
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const smooth = (a: number, b: number, x: number): number => {
  const t = clamp((x - a) / Math.max(0.0001, b - a));
  return t * t * (3 - 2 * t);
};
export const dist = (a: number, b: number, c: number, d: number): number => Math.hypot(a - c, b - d);
export const coreEvidenceCount = (evidence: Partial<Record<EvidenceKey, true>>): number =>
  CORE_EVIDENCE.reduce((n, key) => n + (evidence[key] ? 1 : 0), 0);
